import React, { useEffect, useState, useCallback } from 'react';
import { Drawer, List, Typography, Box, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import SidebarHeader from './components/SidebarHeader';
import SidebarMenuItem from './components/SidebarMenuItem';
import UIToggleSwitch from '../UIToggleSwitch/UIToggleSwitch';
import { RootState } from '../../store';
import { useDispatch, useSelector } from 'react-redux';
import { isNull } from 'lodash';
import { getDashboardUser } from '../../store/dashboardUser/actionCreators';
import { getFacilityRoleById, getPrivilegeById } from '../utils';
import { UserRoles } from '../constants';
import LoadingSpinner from '../../components/Shared/LoadingSpinner';
import {
  bottomMenuConfig,
  mainMenuConfig,
} from './components/SidebarMenuConfig';
import DataSyncStatus from './components/DataSyncStatus';
import { lightBlack, warningYellow } from '../../mainTheme';

const drawerWidth = 240;
const collapsedWidth = 64;

const stateSelector = (state: RootState) => {
  const { dashboardUser } = state;
  const { dashboardUserInfo } = dashboardUser;
  return {
    dashboardUserInfo,
    isLoading: dashboardUser.isLoading,
  };
};

const Sidebar: React.FC = () => {
  const { dashboardUserInfo, isLoading } = useSelector(stateSelector);
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const currentRole = dashboardUserInfo?.facility_role_id
    ? (getFacilityRoleById(
        dashboardUserInfo.facility_role_id ?? 0,
      ) as UserRoles)
    : (getPrivilegeById(dashboardUserInfo?.privilege_id ?? 0) as UserRoles);

  const [open, setOpen] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState<string>(location.pathname);

  const handleDrawerToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  useEffect(() => {
    setSelectedMenu(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (isNull(dashboardUserInfo)) {
      dispatch(getDashboardUser());
    }
  }, [dashboardUserInfo, dispatch]);

  const menuConfig =
    mainMenuConfig[currentRole as keyof typeof mainMenuConfig] ||
    mainMenuConfig[UserRoles.FACILITY_ADMIN];
  let { heading, items } = menuConfig;

  // For Location Admin, replace the Facility menu route with IDs so it navidates directy to Location Page
  if (currentRole === UserRoles.FACILITY_ADMIN && Array.isArray(items)) {
    items = items.map((item) => {
      if (
        item.text === 'Facility' &&
        item.to.startsWith(
          '/v2/facilities/__FACILITY_ID__/location/__LOCATION_ID__',
        )
      ) {
        const facility_id = dashboardUserInfo?.parent_facility_id;
        const location_id = dashboardUserInfo?.facility_id;
        let to = '/v2/facilities';
        if (facility_id && location_id) {
          to = `/v2/facilities/${facility_id}/location/${location_id}`;
        }
        return { ...item, to };
      }
      return item;
    });
  }

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: open ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}
    >
      <div>
        <SidebarHeader open={open} handleDrawerToggle={handleDrawerToggle} />
        {!isLoading ? (
          <List>
            {items.slice(0, menuConfig.topSlice).map((item) => (
              <Box px="16px" key={item.text}>
                <SidebarMenuItem
                  icon={item.icon}
                  text={item.text}
                  to={item.to}
                  open={open}
                  selected={
                    item.to === '/'
                      ? selectedMenu === item.to
                      : selectedMenu.startsWith(item.to)
                  }
                  selectedIcon={item?.selectedIcon}
                />
              </Box>
            ))}

            {open && (
              <Typography
                sx={{
                  margin: theme.spacing(2),
                  color: lightBlack,
                  fontSize: '16px',
                  fontWeight: 500,
                  lineHeight: '24px',
                  letterSpacing: '0.2px',
                }}
              >
                {heading}
              </Typography>
            )}

            <List component="div" disablePadding>
              {items.slice(menuConfig.topSlice).map((item) => (
                <Box px="16px" key={item.text}>
                  <SidebarMenuItem
                    icon={item.icon}
                    text={item.text}
                    to={item.to}
                    open={open}
                    selected={location.pathname.startsWith(item.to)}
                    selectedIcon={item?.selectedIcon}
                  />
                </Box>
              ))}
            </List>
          </List>
        ) : (
          <LoadingSpinner />
        )}
      </div>
      <Box p={2}>
        {bottomMenuConfig.visibleFor.includes(currentRole) && (
          <Box mb={2} display="flex" alignItems="center">
            <SidebarMenuItem
              icon={bottomMenuConfig.icon}
              text={bottomMenuConfig.text}
              to={bottomMenuConfig.to}
              open={open}
              selected={location.pathname.startsWith(bottomMenuConfig.to)}
            />
            <Chip
              label={bottomMenuConfig.alertCount}
              sx={{
                backgroundColor: warningYellow,
              }}
            />
          </Box>
        )}
        <DataSyncStatus currentRole={currentRole} />
        {/* Pharmacy techs are modern-design only — hide the Classic/Modern toggle
            so they can't flip into a legacy UI that has no pharmacy view. */}
        {currentRole !== UserRoles.PHARMACY_TECH && <UIToggleSwitch />}
      </Box>
    </Drawer>
  );
};

export default React.memo(Sidebar);
